import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ReportView } from '../../api/models/Report';
import { getAllCategories } from '../../redux/categoriesSlice';
import { AppDispatch, RootState } from '../../redux/store';

import { EditIcon, SpinnerIcon, TrashIcon } from '../shared/Icons/index';

import { hasFullAccess } from '../utils/permissionLevels';

import { DeleteReport, GetReportById, EditReport as UpdateReport } from '../../api/services/reports';

import { Category } from '../../api/models/Category';

import { DeleteReport as DeleteReportModal } from './DeleteReport/DeleteReport';

import { EditReport } from './EditReport/EditReport';
import './ReportDasboard.scss';

import { EditReportModel } from './EditReport/editReportInterfaces';

export const ReportDashboard = () => {
  const params = useParams();
  const reportId = params.reportId ?? '';
  const categoryId = params.categoryId ?? '';
  const [dashboardSrc, setDashboardSrc] = useState('');
  const [deleteReportModalOpened, setDeleteReportModalOpened] = useState(false);
  const [editReportModalOpened, setEditReportModalOpened] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [report, setReport] = useState<ReportView>({
    id: '',
    link: '',
    name: '',
    categoryId: '',
  });
  const [currentCategory, setCurrentCategory] = useState<Category>({
    id: '',
    items: [],
    name: '',
  });
  const [dashboardName, setDashboardName] = useState('');
  const { categories } = useSelector((state: RootState) => state.categories);
  const { decodedToken } = useSelector((state: RootState) => state.auth);
  const reportPermissions = decodedToken?.features?.REPORTS?.permissions;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const fetchReport = useCallback(async () => {
    if (!reportId || reportId === '') return;

    // eslint-disable-next-line promise/always-return
    await GetReportById(reportId).then((result) => {
      setLoading(false);
      setReport(result);
      setDashboardSrc(result.link);
    });
  }, [reportId]);

  const handleDeleteReport = useCallback(async () => {
    await DeleteReport(report.id);
    await dispatch(getAllCategories());
    toast.info(`${report.name} has been deleted.`);
    navigate('/');
  }, [dispatch, navigate, report.id, report.name]);

  const handleEditReport = useCallback(
    async (editedReport: EditReportModel) => {
      const result = await UpdateReport(editedReport);
      await dispatch(getAllCategories());
      void fetchReport();
      return navigate(`/${result.categoryId}/${result.id}`);
    },
    [dispatch, fetchReport, navigate]
  );

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    categories.map((c) => {
      if (c.id === categoryId) {
        setCurrentCategory(c);
        return;
      }
    });
  }, [categories, categoryId]);

  useEffect(() => {
    if (currentCategory.name && report.name) {
      setDashboardName(`${currentCategory.name} > ${report.name}`);
    }
  }, [currentCategory.name, report.name]);

  const handleDeleteReportOpen = useCallback(() => {
    setDeleteReportModalOpened(true);
  }, []);

  const handleDeleteReportClose = useCallback(() => {
    setDeleteReportModalOpened(false);
  }, []);

  const handleEditReportOpen = useCallback(() => {
    setEditReportModalOpened(true);
  }, []);

  const handleEditReportClose = useCallback(() => {
    setEditReportModalOpened(false);
  }, []);

  return (
    <div className='report-dashboard-container'>
      <header>
        <h3 className='report-title'>{dashboardName}</h3>
        <div className='controls' data-testid='report-dashboard-controls'>
          {hasFullAccess(reportPermissions) ? (
            <button className='edit' data-testid='edit-report-button' type='button' onClick={handleEditReportOpen}>
              <p className='text'>Edit</p>
              <EditIcon />
            </button>
          ) : (
            <></>
          )}
          {hasFullAccess(reportPermissions) ? (
            <button
              className='delete'
              data-testid='delete-report-button'
              type='button'
              onClick={handleDeleteReportOpen}
            >
              <p className='text'>Delete</p>
              <TrashIcon />
            </button>
          ) : (
            <></>
          )}
        </div>
      </header>

      {loading ? (
        <SpinnerIcon />
      ) : (
        <iframe className='dashboard' src={dashboardSrc} frameBorder={0} allow='fullscreen' title={report.name} />
      )}

      {deleteReportModalOpened ? (
        <DeleteReportModal
          dashboardName={dashboardName}
          handleCancel={handleDeleteReportClose}
          handleDelete={handleDeleteReport}
        />
      ) : (
        <></>
      )}

      {editReportModalOpened ? (
        <EditReport
          id={report.id}
          categoryId={categoryId}
          categoryName={currentCategory.name}
          reportName={report.name}
          handleCancel={handleEditReportClose}
          editReport={handleEditReport}
        />
      ) : (
        <></>
      )}
    </div>
  );
};
